import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridComparatorFn,
  GridFilterOperator,
  GridValidRowModel,
} from '@mui/x-data-grid';

import { ListSummary } from '@/types/shared';

const EMPTY_FILTER_OPERATOR_VALUES = new Set(['isEmpty', 'isNotEmpty']);

export const withoutEmptyFilterOperators = (
  operators: GridFilterOperator[]
): GridFilterOperator[] =>
  operators.filter((operator) => !EMPTY_FILTER_OPERATOR_VALUES.has(operator.value));

export const getFilterOperatorsForColumnType = (
  type?: GridColDef['type']
): GridFilterOperator[] => {
  switch (type) {
    case 'number':
      return withoutEmptyFilterOperators(getGridNumericOperators());
    case 'date':
    case 'dateTime':
      return withoutEmptyFilterOperators(getGridDateOperators());
    case 'singleSelect':
      return withoutEmptyFilterOperators(getGridSingleSelectOperators());
    default:
      return withoutEmptyFilterOperators(getGridStringOperators());
  }
};

export const withNonEmptyFilterOperators = <T extends GridColDef>(column: T): T => ({
  ...column,
  filterOperators: column.filterOperators ?? getFilterOperatorsForColumnType(column.type),
});

const ASSIGNEES_EXCLUDED_FILTER_OPERATOR_VALUES = new Set([
  'equals',
  'doesNotEqual',
  'startsWith',
  'endsWith',
  'isAnyOf',
]);

/** String filters for the Assignees column: contains / does not contain only. */
export const assigneesFilterOperators: GridFilterOperator[] = getGridStringOperators().filter(
  (operator) => !ASSIGNEES_EXCLUDED_FILTER_OPERATOR_VALUES.has(operator.value)
);

const COORDINATORS_ALLOWED_FILTER_OPERATOR_VALUES = new Set(['contains', 'doesNotContain']);

/** String filters for the Coordinators column: contains / does not contain only. */
export const coordinatorsFilterOperators: GridFilterOperator[] = getGridStringOperators().filter(
  (operator) => COORDINATORS_ALLOWED_FILTER_OPERATOR_VALUES.has(operator.value)
);

const CONTACTS_TOTAL_FILTER_OPERATOR_VALUES = new Set(['=', '!=', '>', '>=', '<', '<=', 'isAnyOf']);

/**
 * Numeric filters for Contacts x/y columns — compares against the denominator (total count), not the
 * displayed "x/y" string.
 */
export const createContactsTotalFilterOperators = <R extends GridValidRowModel, V = unknown, F = V>(
  getTotal: (row: R) => number
): GridFilterOperator<R, V, F>[] =>
  withoutEmptyFilterOperators(getGridNumericOperators())
    .filter((operator) => CONTACTS_TOTAL_FILTER_OPERATOR_VALUES.has(operator.value))
    .map((operator) => ({
      ...operator,
      getApplyFilterFn: (filterItem, column) => {
        const compareTotal = operator.getApplyFilterFn(
          filterItem,
          column as GridColDef<GridValidRowModel>
        );
        if (!compareTotal) {
          return null;
        }
        return (value, row, col, apiRef) => compareTotal(getTotal(row), row, col, apiRef);
      },
    })) as GridFilterOperator<R, V, F>[];

/**
 * Sort comparator for Contacts x/y columns — compares by the denominator (total count) numerically,
 * not by the displayed "x/y" string.
 *
 * When totals are equal, compares by numerator ascending (called/complete count).
 */
export const createContactsTotalSortComparator =
  <R extends GridValidRowModel>(
    getTotal: (row: R) => number,
    getNumerator: (row: R) => number
  ): GridComparatorFn<string> =>
  (_v1, _v2, p1, p2) => {
    const r1 = p1.api.getRow(p1.id) as R | null;
    const r2 = p2.api.getRow(p2.id) as R | null;
    const t1 = r1 ? (getTotal(r1) ?? 0) : 0;
    const t2 = r2 ? (getTotal(r2) ?? 0) : 0;
    if (t1 !== t2) return t1 < t2 ? -1 : 1;

    const n1 = r1 ? (getNumerator(r1) ?? 0) : 0;
    const n2 = r2 ? (getNumerator(r2) ?? 0) : 0;
    if (n1 !== n2) return n1 < n2 ? -1 : 1;

    return 0;
  };

export const listContactsTotalFilterOperators = createContactsTotalFilterOperators<
  ListSummary,
  string,
  string
>((row) => row.count);

export const listContactsTotalSortComparator = createContactsTotalSortComparator<ListSummary>(
  (row) => row.count,
  (row) => row.count - row.uncalledCount
);
